import PageLoader from '@/components/layouts/PageLoader.tsx';
import { useChangePassword } from '@/remote/user-management/change-password';
import { useMeQuery } from '@/remote/user-management/fetch-me.ts';
import type { User } from '@/types/site.ts';
import { Suspense, useState } from 'react';
import { Modal } from '../modules/Modal';

interface PasswordChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  skipCurrentPassword?: boolean;
  targetUserId?: string; // 변경할 대상 유저 ID (없으면 본인 비밀번호 변경)
}

export default function PasswordChangeModal({
  isOpen,
  onClose,
  skipCurrentPassword = false,
  targetUserId,
}: Readonly<PasswordChangeModalProps>) {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const handleInputChange =
    (field: keyof typeof formData) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));

      // 입력 시 에러 메시지 초기화
      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: '',
        }));
      }
    };

  // 비밀번호 유효성 검증
  const validatePassword = (password: string): string => {
    if (password.length < 8) {
      return '비밀번호는 최소 8자 이상이어야 합니다.';
    }
    if (!/(?=.*[a-zA-Z])/.test(password)) {
      return '비밀번호는 영문자를 포함해야 합니다.';
    }
    if (!/(?=.*\d)/.test(password)) {
      return '비밀번호는 숫자를 포함해야 합니다.';
    }
    if (!/(?=.*[!@#$%^&*(),.?":{}|<>])/.test(password)) {
      return '비밀번호는 특수문자를 포함해야 합니다.';
    }
    return '';
  };

  const validateForm = (): boolean => {
    const newErrors = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    };

    // 현재 비밀번호 확인 (skipCurrentPassword가 false일 때만)
    if (!skipCurrentPassword && !formData.currentPassword.trim()) {
      newErrors.currentPassword = '현재 비밀번호를 입력해주세요.';
    }

    // 새 비밀번호 유효성 검증
    if (!formData.newPassword.trim()) {
      newErrors.newPassword = '새 비밀번호를 입력해주세요.';
    } else {
      const passwordError = validatePassword(formData.newPassword);
      if (passwordError) {
        newErrors.newPassword = passwordError;
      }
    }

    // 비밀번호 확인 검증
    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = '비밀번호 확인을 입력해주세요.';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = '새 비밀번호가 일치하지 않습니다.';
    }

    // 현재 비밀번호와 새 비밀번호 동일성 검증 (skipCurrentPassword가 false일 때만)
    if (
      !skipCurrentPassword &&
      formData.currentPassword &&
      formData.newPassword &&
      formData.currentPassword === formData.newPassword
    ) {
      newErrors.newPassword = '새 비밀번호는 현재 비밀번호와 달라야 합니다.';
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error !== '');
  };

  const handleClose = () => {
    // 모달 닫을 때 폼 초기화
    setFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setErrors({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="비밀번호 변경"
      size="md"
      closeOnBackdropClick
    >
      <Suspense fallback={<PageLoader />}>
        <DataFetchLayer
          formData={formData}
          validateForm={validateForm}
          skipCurrentPassword={skipCurrentPassword}
          handleClose={handleClose}
          setErrors={setErrors}
          targetUserId={targetUserId}
        >
          {({ changePasswordMutation, handleSubmit }) => (
            <div className="p-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                {!skipCurrentPassword && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      현재 비밀번호
                    </label>
                    <input
                      type="password"
                      value={formData.currentPassword}
                      onChange={handleInputChange('currentPassword')}
                      className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.currentPassword
                          ? 'border-red-300 focus:ring-red-500'
                          : 'border-gray-300'
                      }`}
                      required
                      disabled={changePasswordMutation.isPending}
                    />
                    {errors.currentPassword && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.currentPassword}
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    새 비밀번호
                  </label>
                  <input
                    type="password"
                    value={formData.newPassword}
                    onChange={handleInputChange('newPassword')}
                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.newPassword
                        ? 'border-red-300 focus:ring-red-500'
                        : 'border-gray-300'
                    }`}
                    required
                    disabled={changePasswordMutation.isPending}
                  />
                  {errors.newPassword && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.newPassword}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    8자 이상, 영문자, 숫자, 특수문자를 포함해야 합니다.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    새 비밀번호 확인
                  </label>
                  <input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={handleInputChange('confirmPassword')}
                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.confirmPassword
                        ? 'border-red-300 focus:ring-red-500'
                        : 'border-gray-300'
                    }`}
                    required
                    disabled={changePasswordMutation.isPending}
                  />
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.confirmPassword}
                    </p>
                  )}
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="submit"
                    disabled={changePasswordMutation.isPending}
                    className={`flex-1 py-2 rounded-lg transition-colors duration-200 flex items-center justify-center ${
                      changePasswordMutation.isPending
                        ? 'bg-blue-400 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700'
                    } text-white`}
                  >
                    {changePasswordMutation.isPending ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-3 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        변경 중...
                      </>
                    ) : (
                      '비밀번호 업데이트'
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={changePasswordMutation.isPending}
                    className={`flex-1 border border-gray-300 py-2 rounded-lg transition-colors duration-200 ${
                      changePasswordMutation.isPending
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    취소
                  </button>
                </div>
              </form>
            </div>
          )}
        </DataFetchLayer>
      </Suspense>
    </Modal>
  );
}

const DataFetchLayer = ({
  children,
  formData,
  validateForm,
  skipCurrentPassword,
  handleClose,
  setErrors,
  targetUserId,
}: {
  formData: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  };
  validateForm: () => boolean;
  skipCurrentPassword: boolean;
  handleClose: () => void;
  setErrors: React.Dispatch<
    React.SetStateAction<{
      currentPassword: string;
      newPassword: string;
      confirmPassword: string;
    }>
  >;
  targetUserId?: string;
  children: ({
    me,
    changePasswordMutation,
    handleSubmit,
  }: {
    me: User;
    changePasswordMutation: ReturnType<typeof useChangePassword>;
    handleSubmit: (e: React.FormEvent) => Promise<void>;
  }) => React.ReactNode;
}) => {
  const { data: me } = useMeQuery();

  // targetUserId가 있으면 해당 유저의 비밀번호를 변경, 없으면 본인 비밀번호 변경
  const changePasswordMutation = useChangePassword({
    userId: targetUserId || me?.id || '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const requestData = skipCurrentPassword
        ? { newPassword: formData.newPassword }
        : {
            currentPassword: formData.currentPassword,
            newPassword: formData.newPassword,
          };

      await changePasswordMutation.mutateAsync(requestData);

      // 성공 시 폼 초기화 및 모달 닫기
      handleClose();

      // 성공 메시지 표시 (실제로는 토스트 메시지 등을 사용)
      alert('비밀번호가 성공적으로 변경되었습니다.');
    } catch (error) {
      console.error('비밀번호 변경 실패:', error);
      setErrors((prev) => ({
        ...prev,
        currentPassword: '현재 비밀번호가 올바르지 않습니다.',
      }));
    }
  };

  if (!me) {
    return <PageLoader />;
  }

  return children({ me, changePasswordMutation, handleSubmit });
};
